import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { Avatar, Box, CheckOutline, Flex, IconButton, PlusOutline, SkeletonText, Text } from '@bonusly/design-system';

import { useUsers } from '../../../queries/user';
import type { UserDetail } from '../../../types/user';
import type { ApprovalDecision } from '../hooks/use-bizy-global-chat';

/** Wire shape for one entry in a meeting_people_card payload. Keys are
 *  camelCase at the JS boundary (matching Ruby's `to_h`).
 *
 *  HABITS-1240 round 7: the per-person payload is `userId`-only — display
 *  data (name, username, avatar) is resolved on the frontend via the
 *  identity service. `name`, `username`, and `avatarUrl` are still declared
 *  here for backward compatibility with pre-round-7 persisted cards, but
 *  the component prefers the hydrated identity result when both are
 *  available. This way reload after a user renames themselves shows the
 *  current display name, not a stale string pinned to the card. */
export interface MeetingPerson {
	userId?: string;
	participantKey?: string;
	subtitle?: string | null;
	cta?: {
		label?: string;
		href?: string;
	};
	/** Server-supplied completion status. "completed" renders the green
	 *  state; anything else falls through to the default Create CTA. */
	status?: string | null;
	// Legacy display fields — present on pre-round-7 persisted cards only.
	// New tool results never include them; keep reading here so older
	// ChatHistory rows still render a name while the identity fetch runs.
	name?: string;
	username?: string;
	avatarUrl?: string | null;
}

export interface BizyMeetingPeopleCardProps {
	/** Optional row title. Rendered above the vertical stack. */
	title?: string;
	/** The people to render. Empty / missing lists suppress the whole card. */
	people: MeetingPerson[];
	/** Position of this card within its message — used by `onChoiceUpdate`
	 *  so the parent can persist a touched-card flag in `nodeChoices` for
	 *  cross-reload state recovery. */
	cardIndex?: number;
	/** Stable key for the message owning this card. */
	messageKey?: string;
	/** Per-card decision (set after first interaction). When `'approved'`,
	 *  the card knows it's been touched in this session — useful if the
	 *  parent wants to render anything differently. The component itself
	 *  keeps per-person granularity in local state and does not re-flip
	 *  every row from a card-level approval (the whole card has multiple
	 *  rows; only the rows the user actually clicked should be green). */
	choice?: ApprovalDecision;
	/** Records the per-card decision back to the parent so it survives
	 *  reload. Called once per card lifetime — the first time any row's
	 *  `+` is clicked. */
	onChoiceUpdate?: (messageKey: string, cardIndex: number, choice: ApprovalDecision) => void;
}

/**
 * Vertical stack of compact per-person rows, used by the
 * `suggest_people_to_meet` tool (HABITS-1240 round 6+7). The previous
 * horizontal-scroll layout under-utilized the side panel's width and
 * left the per-person reason text truncated to a single line; the
 * vertical row layout gives each person 2–3 lines of reason space at
 * the cost of compressing the avatar/CTA to a single icon column.
 *
 * Per-row layout (left → right):
 *  - 40px avatar (initials while identity loads)
 *  - flex column: name (semibold sm) + reason (xs, wraps to 2–3 lines)
 *  - 32px `+` IconButton CTA (turns into a green checkmark when completed)
 *
 * Round 7 changes:
 *  - The whole row is a `<Link>` — clicking anywhere in the row (not
 *    just the `+` icon) creates the 1:1. Preserves cmd-click /
 *    middle-click for "open in new tab" because react-router-dom's Link
 *    renders a real `<a>`.
 *  - On hover the row background subtly tints (`bg.subtle`); completed
 *    rows keep their green treatment and no longer react to hover.
 *  - Display data (name, username, avatar) is fetched via `useUsers`
 *    from the hydrated userIds. While loading we show initials from any
 *    legacy payload fields and a skeleton where the name would be.
 *
 * Tradeoff (v1 scope): cross-session per-PERSON persistence is NOT
 * plumbed through `nodeChoices` (which is per-card, not per-person).
 * On a fresh session the card re-renders from server state — that's
 * fine because the orchestrator filters out existing 1:1 partners on
 * every fresh tool call, so a once-clicked person should not reappear
 * in a later turn. We DO call `onChoiceUpdate` once on first click so
 * the parent can mark the whole card as "touched" for analytics or any
 * future per-card display state.
 */
export function BizyMeetingPeopleCard({ title, people, cardIndex, messageKey, choice: externalChoice, onChoiceUpdate }: BizyMeetingPeopleCardProps) {
	const { t } = useTranslation('admin_bizy');
	const [completedLocally, setCompletedLocally] = useState<Record<number, boolean>>({});
	const hasRecordedChoiceRef = useRef(externalChoice === 'approved');

	// Sync the dedup ref with late-arriving external choice. If the
	// component mounts before `nodeChoices` is hydrated (externalChoice
	// undefined at mount) and the parent later re-renders with
	// externalChoice === 'approved', the mount-time initial value would
	// stay `false` and the next click would fire a redundant
	// `onChoiceUpdate`. Monotonic: once true, never flipped back — a
	// once-recorded choice stays recorded for the lifetime of the card.
	useEffect(() => {
		if (externalChoice === 'approved') {
			hasRecordedChoiceRef.current = true;
		}
	}, [externalChoice]);

	// Batch-resolve display data for all userIds the card carries. `useUsers`
	// sends a single POST /user_management/users/bulk and returns transformed
	// UserDetail[]; we index by id for O(1) row lookup. Missing ids (user
	// deactivated, cross-company, etc.) silently degrade to initials.
	const userIds = useMemo(() => {
		const ids = people.map((p) => p.userId).filter((id): id is string => typeof id === 'string' && id.length > 0);
		// Dedupe to keep the query key stable and the bulk payload small.
		return Array.from(new Set(ids));
	}, [people]);
	const { data: fetchedUsers, isLoading: isLoadingUsers } = useUsers(userIds.length > 0 ? userIds : undefined);
	const usersById = useMemo(() => {
		const map = new Map<string, UserDetail>();
		fetchedUsers?.forEach((u) => {
			if (u.id) map.set(u.id, u);
		});
		return map;
	}, [fetchedUsers]);

	const handleRowInteract = useCallback(
		(index: number) => {
			setCompletedLocally((prev) => ({ ...prev, [index]: true }));
			if (!hasRecordedChoiceRef.current && messageKey && cardIndex !== undefined && onChoiceUpdate) {
				// Flip the ref BEFORE calling out so any synchronous re-entry
				// (or a sibling row click batched into the same React tick)
				// sees the recorded state and skips the duplicate update.
				hasRecordedChoiceRef.current = true;
				onChoiceUpdate(messageKey, cardIndex, 'approved');
			}
		},
		[messageKey, cardIndex, onChoiceUpdate]
	);

	const hasPeople = Array.isArray(people) && people.length > 0;
	if (!hasPeople) return null;

	return (
		<motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
			<Box my={2}>
				{title && (
					<Text fontSize="xs" fontWeight="semibold" color="text.softer" textTransform="uppercase" letterSpacing="wider" mb={2}>
						{title}
					</Text>
				)}

				<Flex direction="column" gap={2}>
					{people.map((person, index) => {
						const isCompleted = person.status === 'completed' || completedLocally[index] === true;
						const hydrated = person.userId ? usersById.get(person.userId) : undefined;
						// Prefer hydrated identity data, but fall back to legacy
						// payload fields so pre-round-7 persisted cards keep
						// rendering until the fetch resolves (or forever, if
						// the fetch fails).
						const displayName = hydrated?.displayName ?? person.name ?? person.username ?? '';
						const avatarSrc = hydrated?.profilePic ?? person.avatarUrl ?? undefined;
						const nameForAria = displayName || person.userId || '';
						const isLoadingThisRow = isLoadingUsers && !hydrated && !person.name && !person.username;

						return (
							<PersonRow
								key={`${person.participantKey ?? person.userId ?? index}`}
								displayName={displayName}
								avatarSrc={avatarSrc}
								subtitle={person.subtitle ?? null}
								href={person.cta?.href}
								isCompleted={isCompleted}
								isLoadingDisplayData={isLoadingThisRow}
								completedAriaLabel={t('meeting_people_card.completed_aria_label', { name: nameForAria })}
								createAriaLabel={t('meeting_people_card.create_aria_label', { name: nameForAria })}
								onInteract={() => handleRowInteract(index)}
							/>
						);
					})}
				</Flex>
			</Box>
		</motion.div>
	);
}

interface PersonRowProps {
	displayName: string;
	avatarSrc: string | undefined;
	subtitle: string | null;
	href: string | undefined;
	isCompleted: boolean;
	isLoadingDisplayData: boolean;
	completedAriaLabel: string;
	createAriaLabel: string;
	/** Fired on row activation (Link click). The parent updates per-row
	 *  completion state + one-time choice recording; the Link itself
	 *  handles the navigation. */
	onInteract: () => void;
}

function PersonRow({
	displayName,
	avatarSrc,
	subtitle,
	href,
	isCompleted,
	isLoadingDisplayData,
	completedAriaLabel,
	createAriaLabel,
	onInteract,
}: PersonRowProps) {
	// A row without a navigable CTA is inert — render the same visual
	// shell but no Link wrapper / hover affordance. Defensive: the tool
	// always sets `cta.href`, this branch is just a safety net for
	// malformed persisted data.
	if (!href) {
		return (
			<RowShell
				displayName={displayName}
				avatarSrc={avatarSrc}
				subtitle={subtitle}
				isCompleted={isCompleted}
				isLoadingDisplayData={isLoadingDisplayData}
				showHoverBackground={false}
			>
				<IconButton
					aria-label={isCompleted ? completedAriaLabel : createAriaLabel}
					icon={isCompleted ? <CheckOutline boxSize={4} color="success.text" /> : <PlusOutline boxSize={4} />}
					size="sm"
					variant="ghost"
					colorScheme={isCompleted ? 'success' : 'primary'}
					isDisabled
					flexShrink={0}
					pointerEvents="none"
				/>
			</RowShell>
		);
	}

	// Completed rows are navigable (the user can still jump to their
	// 1:1) but don't react to hover — the green success treatment is
	// the dominant visual signal. Mirrors the same precedence rule the
	// tests pin.
	if (isCompleted) {
		return (
			<Box
				as={RouterLink}
				to={href}
				display="block"
				textDecoration="none"
				_hover={{ textDecoration: 'none' }}
				aria-label={completedAriaLabel}
				// Still record the interaction so `onChoiceUpdate` fires on
				// subsequent navigations. The parent's `completedLocally`
				// map is already `true` for this row; no-op in practice.
				onClick={onInteract}
			>
				<RowShell
					displayName={displayName}
					avatarSrc={avatarSrc}
					subtitle={subtitle}
					isCompleted
					isLoadingDisplayData={isLoadingDisplayData}
					showHoverBackground={false}
				>
					<RowAffordanceIcon icon={<CheckOutline boxSize={4} color="success.text" />} />
				</RowShell>
			</Box>
		);
	}

	return (
		<Box as={RouterLink} to={href} display="block" textDecoration="none" _hover={{ textDecoration: 'none' }} aria-label={createAriaLabel} onClick={onInteract}>
			<RowShell
				displayName={displayName}
				avatarSrc={avatarSrc}
				subtitle={subtitle}
				isCompleted={false}
				isLoadingDisplayData={isLoadingDisplayData}
				showHoverBackground
			>
				<RowAffordanceIcon icon={<PlusOutline boxSize={4} />} />
			</RowShell>
		</Box>
	);
}

/**
 * Decorative icon slot used inside a navigable row. We render the icon
 * itself rather than an IconButton because the whole row IS the link
 * (and the link carries the accessible label) — nesting an interactive
 * IconButton inside would create a focusable element that duplicates
 * the link's activation target and forces a redundant aria-label.
 */
function RowAffordanceIcon({ icon }: { icon: React.ReactNode }) {
	return (
		<Flex as="span" aria-hidden="true" align="center" justify="center" boxSize={8} flexShrink={0} color="inherit">
			{icon}
		</Flex>
	);
}

interface RowShellProps {
	displayName: string;
	avatarSrc: string | undefined;
	subtitle: string | null;
	isCompleted: boolean;
	isLoadingDisplayData: boolean;
	/** When true, the outer box adds a subtle `bg.subtle` tint on hover.
	 *  Completed / inert rows pass `false`. */
	showHoverBackground: boolean;
	children: React.ReactNode;
}

function RowShell({ displayName, avatarSrc, subtitle, isCompleted, isLoadingDisplayData, showHoverBackground, children }: RowShellProps) {
	// `data-state` gives the test layer a stable, framework-agnostic signal
	// for which visual treatment the row is in. The three states are
	// mutually exclusive and mirror the branching in `PersonRow`.
	const rowState = isCompleted ? 'completed' : showHoverBackground ? 'interactive' : 'inert';
	return (
		<Box
			data-state={rowState}
			border="1px solid"
			borderColor={isCompleted ? 'success.border' : 'border.soft'}
			bg={isCompleted ? 'success.bg' : 'bg.surface'}
			borderRadius={12}
			px={3}
			py={2.5}
			transition="background 0.15s, border-color 0.15s"
			_hover={showHoverBackground ? { bg: 'bg.subtle' } : undefined}
		>
			<Flex align="center" gap={3}>
				<Avatar name={displayName} src={avatarSrc} size="sm" flexShrink={0} />

				<Flex direction="column" flex="1 1 auto" minW={0} gap={0.5}>
					{isLoadingDisplayData ? (
						<SkeletonText noOfLines={1} skeletonHeight="3" width="60%" />
					) : (
						<Text fontSize="sm" fontWeight="semibold" color="text.default" noOfLines={1}>
							{displayName}
						</Text>
					)}
					{subtitle && (
						<Text fontSize="xs" color="text.soft" noOfLines={3} lineHeight="short">
							{subtitle}
						</Text>
					)}
				</Flex>

				{children}
			</Flex>
		</Box>
	);
}
